import React, { useState, useEffect, useRef } from 'react';
import {
  ActionButton,
  MenuTrigger,
  RangeCalendar,
  Calendar,
  View,
  Text,
  Flex
} from '@adobe/react-spectrum';
import { parseDate, CalendarDate } from '@internationalized/date';
import ChevronLeft from '@spectrum-icons/workflow/ChevronLeft';
import ChevronRight from '@spectrum-icons/workflow/ChevronRight';
import CalendarIcon from '@spectrum-icons/workflow/Calendar';
import { getQuarter, formatDate } from 'utils/helpers';
import LeftArrowIcon from '../../../assets/svgIcons/LeftArrowIcon';
import RightArrowIcon from '../../../assets/svgIcons/RightArrowIcon';
import { useHover } from 'react-aria';
import { TBDNASelector } from './TBDNASelector';
import { useCalendarFocusedValue } from './useCalendarFocusedValue';
import {
  YEARS_BACK,
  YEARS_FORWARD,
  getCurrentFiscalQuarterAndYear,
  getCurrentMonthsArray,
  getCurrentQuartersArray,
  getCurrentMonthPosition,
  getCurrentQuarterPosition
} from './calendarRangeHelpers';

interface DateRange {
  start: CalendarDate;
  end: CalendarDate;
}

interface CalendarRangeProps {
  defaultRange?: DateRange;
  defaultDate?: CalendarDate;
  defaultQuarter?: { quarter: number; year: number };
  defaultMonth?: { month: string; year: number };
  onCalendarRangeChange?: (range: DateRange) => void;
  onCalendarDateChange?: (
    date: CalendarDate,
    isQuarterSelection?: boolean,
    selectedQuarter?: { quarter: number; year: number },
    isMonthSelection?: boolean,
    selectedMonth?: { month: string; year: number },
    specialValue?: 'TBD' | 'N/A' | null
  ) => void;
  skipTriggerButton?: boolean;
  disableYearsAndQuarters?: boolean;
  hideYearList?: boolean;
  hideQuarterList?: boolean;
  hideMonthList?: boolean;
  showTBDNA?: boolean; // Changed from hideTBDNA to showTBDNA for better semantics
  mode?: 'range' | 'single';
  defaultSpecialValue?: 'TBD' | 'N/A' | null;
  disablePastDate?: boolean;
  enforceEndDateAfterStart?: boolean;
  disableNA?: boolean;
  disableTBD?: boolean;
}

interface CalendarContentProps {
  displayedQuarterIndex: number;
  displayedMonthIndex: number;
  disableYearsAndQuarters: boolean;
  hideYearList: boolean;
  hideQuarterList: boolean;
  hideMonthList: boolean;
  handleScrollQuarters: (direction: 'left' | 'right') => void;
  handleScrollMonths: (direction: 'left' | 'right') => void;
  quartersArray: { quarter: number; year: number }[];
  monthsArray: { month: string; year: number; monthIndex: number }[];
  quarterRange: { left: number; width: number } | null;
  monthRange: { left: number; width: number } | null;
  startQuarter: { quarter: number; year: number } | null;
  endQuarter: { quarter: number; year: number } | null;
  hoveringQuarter: { quarter: number; year: number } | null;
  activeQuarter: number | null;
  handleQuarterClick: (quarter: number, year: number) => void;
  handleQuarterHover: (quarter: number | null, year: number | null) => void;
  startMonth: number | null;
  endMonth: number | null;
  startMonthYear: number | null;
  endMonthYear: number | null;
  hoveringMonth: number | null;
  hoveringMonthYear: number | null;
  handleMonthClick: (month: number, year: number) => void;
  handleMonthHover: (month: number, year: number) => void;
  mode: 'range' | 'single';
  range: DateRange | undefined;
  handleRangeChange: (newRange: DateRange) => void;
  selectedDate: CalendarDate | undefined;
  handleDateChange: (date: CalendarDate) => void;
  handleYearClick: (year: number) => void;
  activeYear: number | null;
  showTBDNA?: boolean; // Changed from hideTBDNA to showTBDNA
  specialValue: 'TBD' | 'N/A' | null;
  onSpecialValueChange: (value: 'TBD' | 'N/A' | null) => void;
  isDisabled?: boolean;
  disablePastDate?: boolean;
  disableNA?: boolean;
  disableTBD?: boolean;
}

const getDynamicDates = (baseYear: number): DateRange => {
  // Calculate December 1st of the given base year
  const startDate = new Date(baseYear - 1, 11, 1); // December (zero-based index)
  // Calculate November 30th of the following year
  const endDate = new Date(baseYear, 10, 30); // November (zero-based index)

  // Format dates as YYYY-MM-DD

  return {
    start: parseDate(formatDate(startDate)),
    end: parseDate(formatDate(endDate))
  };
};

const getQuarterDates = (baseYear: number, quarter: number = 0): DateRange => {
  let startDate: Date, endDate: Date;

  switch (quarter) {
    case 1: // Q1: Dec - Feb
      startDate = new Date(baseYear - 1, 11, 1); // Dec 1
      endDate = new Date(baseYear, 1, new Date(baseYear, 2, 0).getDate()); // Feb 28/29 depending on leap year
      break;
    case 2: // Q2: Mar - May
      startDate = new Date(baseYear, 2, 1); // Mar 1
      endDate = new Date(baseYear, 4, 31); // May 31
      break;
    case 3: // Q3: Jun - Aug
      startDate = new Date(baseYear, 5, 1); // Jun 1
      endDate = new Date(baseYear, 7, 31); // Aug 31
      break;
    case 4: // Q4: Sep - Nov
      startDate = new Date(baseYear, 8, 1); // Sep 1
      endDate = new Date(baseYear, 10, 30); // Nov 30
      break;
    default:
      startDate = new Date(baseYear - 1, 11, 1); // Dec 1
      endDate = new Date(baseYear, 10, 30); // Nov 30
  }

  return {
    start: parseDate(formatDate(startDate)),
    end: parseDate(formatDate(endDate))
  };
};

const getMonthDates = (baseYear: number, month: number): DateRange => {
  const startDate = new Date(baseYear, month, 1);
  const endDate = new Date(baseYear, month + 1, 0);
  return {
    start: parseDate(formatDate(startDate)),
    end: parseDate(formatDate(endDate))
  };
};

const formatCalendarDate = (date: CalendarDate): string => {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(new Date(date.year, date.month - 1, date.day));
};

// Create a separate Quarter component
const Quarter = ({
  quarter,
  startQuarter,
  endQuarter,
  activeQuarter,
  onQuarterClick,
  onQuarterHover,
  hoveringQuarter,
  year,
  mode
}) => {
  const buttonRef = useRef(null);
  const { hoverProps, isHovered } = useHover({
    onHoverStart: () => {
      // Only trigger hover effects in range mode
      if (mode !== 'single') {
        onQuarterHover(quarter, year);
      }
    },
    onHoverEnd: () => {
      if (mode !== 'single') {
        onQuarterHover(null, null);
      }
    }
  });

  const isStartQuarter =
    startQuarter?.quarter === quarter && startQuarter?.year === year;
  const isEndQuarter =
    endQuarter?.quarter === quarter && endQuarter?.year === year;
  const isHovering =
    hoveringQuarter?.quarter === quarter && hoveringQuarter?.year === year;

  let className = `quarters-list`;
  const { fiscalQuarter, fiscalYear } = getCurrentFiscalQuarterAndYear();
  if (quarter === fiscalQuarter && year === fiscalYear)
    className += ' defaultQuarter';
  if (isStartQuarter) className += ' selectedStart';
  if (isEndQuarter) className += ' selectedEnd';
  if (isHovering) className += ' hovered';

  return (
    <div ref={buttonRef} {...hoverProps} key={`${quarter}-${year}`}>
      <ActionButton
        isQuiet
        aria-label={`Q${quarter} ${year}`}
        data-testid={`calendar-quarter-q${quarter}-${year}`}
        onPress={() => onQuarterClick(quarter, year)}
        UNSAFE_className={className}
      >
        <div className="quarter-label">
          <span>Q{quarter}</span>
          <span className="quarter-year">'{year.toString().slice(-2)}</span>
        </div>
      </ActionButton>
    </div>
  );
};

// Create a separate Month component similar to Quarter
const Month = ({
  month,
  monthIndex,
  year,
  startMonth,
  endMonth,
  startYear,
  endYear,
  onMonthClick,
  onMonthHover,
  hoveringMonth,
  hoveringYear,
  mode
}) => {
  const buttonRef = useRef(null);
  const { hoverProps, isHovered } = useHover({
    onHoverStart: () => {
      // Only trigger hover effects in range mode
      if (mode !== 'single') {
        onMonthHover(monthIndex, year);
      }
    },
    onHoverEnd: () => {
      if (mode !== 'single') {
        onMonthHover(null, null);
      }
    }
  });

  const isStartMonth = startMonth === monthIndex && startYear === year;
  const isEndMonth =
    mode !== 'single' && endMonth === monthIndex && endYear === year;
  const isHoveringMonth =
    mode !== 'single' && hoveringMonth === monthIndex && hoveringYear === year;

  // Only calculate range-related states if in range mode
  const isInBetween =
    mode !== 'single' &&
    startMonth !== null &&
    startYear !== null &&
    ((endMonth !== null &&
      endYear !== null &&
      (((startYear < endYear ||
        (startYear === endYear && startMonth < endMonth)) &&
        (year > startYear || (year === startYear && monthIndex > startMonth)) &&
        (year < endYear || (year === endYear && monthIndex < endMonth))) ||
        ((startYear > endYear ||
          (startYear === endYear && startMonth > endMonth)) &&
          (year < startYear ||
            (year === startYear && monthIndex < startMonth)) &&
          (year > endYear || (year === endYear && monthIndex > endMonth))))) ||
      (endMonth === null &&
        hoveringMonth !== null &&
        hoveringYear !== null &&
        (((startYear < hoveringYear ||
          (startYear === hoveringYear && startMonth < hoveringMonth)) &&
          (year > startYear ||
            (year === startYear && monthIndex > startMonth)) &&
          (year < hoveringYear ||
            (year === hoveringYear && monthIndex < hoveringMonth))) ||
          ((startYear > hoveringYear ||
            (startYear === hoveringYear && startMonth > hoveringMonth)) &&
            (year < startYear ||
              (year === startYear && monthIndex < startMonth)) &&
            (year > hoveringYear ||
              (year === hoveringYear && monthIndex > hoveringMonth))))));

  const isRangeHover =
    mode !== 'single' &&
    startMonth !== null &&
    startYear !== null &&
    endMonth === null &&
    hoveringMonth !== null &&
    hoveringYear !== null &&
    (((startYear < hoveringYear ||
      (startYear === hoveringYear && startMonth < hoveringMonth)) &&
      (year > startYear || (year === startYear && monthIndex > startMonth)) &&
      (year < hoveringYear ||
        (year === hoveringYear && monthIndex < hoveringMonth))) ||
      ((startYear > hoveringYear ||
        (startYear === hoveringYear && startMonth > hoveringMonth)) &&
        (year < startYear || (year === startYear && monthIndex < startMonth)) &&
        (year > hoveringYear ||
          (year === hoveringYear && monthIndex > hoveringMonth))));

  const isRangeHoverEnd =
    mode !== 'single' &&
    startMonth !== null &&
    startYear !== null &&
    endMonth === null &&
    hoveringMonth !== null &&
    hoveringYear !== null &&
    monthIndex === hoveringMonth &&
    year === hoveringYear;

  const isRangeSelected =
    mode !== 'single' &&
    startMonth !== null &&
    startYear !== null &&
    endMonth !== null &&
    endYear !== null &&
    (startYear < endYear || (startYear === endYear && startMonth < endMonth)
      ? (year > startYear || (year === startYear && monthIndex > startMonth)) &&
        (year < endYear || (year === endYear && monthIndex < endMonth))
      : (year < startYear || (year === startYear && monthIndex < startMonth)) &&
        (year > endYear || (year === endYear && monthIndex > endMonth)));

  let className = `months-list`;
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();

  // Only apply default styling if not in a selection state
  if (
    monthIndex === currentMonth &&
    year === currentYear &&
    !isStartMonth &&
    !isEndMonth
  ) {
    className += ' defaultMonth';
  }

  if (isStartMonth) className += ' selectedStart';
  if (isEndMonth) className += ' selectedEnd';
  if (isInBetween) className += ' selected';
  if (isRangeHover) className += ' range-hover';
  if (isRangeHoverEnd) className += ' range-hover-end';
  if (isHoveringMonth) className += ' hovered';

  return (
    <div ref={buttonRef} {...hoverProps} key={`${monthIndex}-${year}`}>
      <ActionButton
        isQuiet
        aria-label={`${month} ${year}`}
        data-testid={`calendar-month-${month.toLowerCase()}-${year}`}
        onPress={() => onMonthClick(monthIndex, year)}
        UNSAFE_className={className}
      >
        <div className="month-label">
          <span>{month}</span>
          <span className="month-year">'{year.toString().slice(-2)}</span>
        </div>
      </ActionButton>
    </div>
  );
};

interface YearProps {
  year: number;
  isDisabled: boolean;
  onYearClick: (year: number) => void;
  activeYear: number | null;
}

const Year = ({ year, isDisabled, onYearClick, activeYear }: YearProps) => {
  const currentYear = new Date().getFullYear();
  let className = 'years-list';
  if (year === currentYear) className += ' defaultYear';
  if (activeYear !== null && year === activeYear) className += ' active-year';
  if (isDisabled) className += ' disabled-year';

  return (
    <ActionButton
      isQuiet
      isDisabled={isDisabled}
      aria-label={year.toString()}
      data-testid={`calendar-year-${year}`}
      onPress={() => !isDisabled && onYearClick(year)}
      UNSAFE_className={className}
    >
      '{year.toString().slice(-2)}
    </ActionButton>
  );
};

// Add these constants before CalendarContent component
const QUARTER_WIDTH = 32;
const QUARTER_GAP = 4;

// Add CalendarContent component before the main CalendarRange component
const CalendarContent = ({
  displayedQuarterIndex,
  displayedMonthIndex,
  disableYearsAndQuarters,
  hideYearList,
  hideQuarterList,
  hideMonthList,
  handleScrollQuarters,
  handleScrollMonths,
  quartersArray,
  monthsArray,
  quarterRange,
  monthRange,
  startQuarter,
  endQuarter,
  hoveringQuarter,
  activeQuarter,
  handleQuarterClick,
  handleQuarterHover,
  startMonth,
  endMonth,
  startMonthYear,
  endMonthYear,
  hoveringMonth,
  hoveringMonthYear,
  handleMonthClick,
  handleMonthHover,
  mode,
  range,
  handleRangeChange,
  selectedDate,
  handleDateChange,
  handleYearClick,
  activeYear,
  showTBDNA,
  specialValue,
  onSpecialValueChange,
  isDisabled,
  disablePastDate,
  disableNA,
  disableTBD
}: CalendarContentProps) => {
  const { focusedValue, onFocusChange } = useCalendarFocusedValue({
    selectedDate,
    startMonth,
    startMonthYear,
    startQuarter
  });

  return (
    <View
      UNSAFE_className="calendar-container"
      data-testid="calendar-container"
    >
      {showTBDNA && (
        <TBDNASelector
          value={specialValue}
          onChange={onSpecialValueChange}
          isDisabled={isDisabled}
          disableNA={disableNA}
          disableTBD={disableTBD}
        />
      )}
      {!disableYearsAndQuarters && !hideYearList && (
        <View UNSAFE_className="years" data-testid="calendar-years">
          <Flex alignItems="center" gap="size-10" justifyContent="center">
            {Array.from(
              { length: YEARS_BACK + YEARS_FORWARD + 1 },
              (_, i) => i - YEARS_BACK
            ).map(offset => {
              const year = new Date().getFullYear() + offset;
              return (
                <Year
                  key={year}
                  year={year}
                  isDisabled={disableYearsAndQuarters}
                  onYearClick={handleYearClick}
                  activeYear={activeYear}
                />
              );
            })}
          </Flex>
        </View>
      )}
      {!disableYearsAndQuarters && !hideQuarterList && (
        <View UNSAFE_className="quarters" data-testid="calendar-quarters">
          <Flex alignItems="center" gap="size-10">
            <ActionButton
              aria-label="Previous year quarters"
              UNSAFE_className="quarter-scroller"
              data-testid="calendar-quarters-previous"
              isQuiet
              isDisabled={disableYearsAndQuarters || displayedQuarterIndex <= 0}
              onPress={() =>
                !disableYearsAndQuarters && handleScrollQuarters('left')
              }
            >
              <LeftArrowIcon size="L" />
            </ActionButton>
            <View
              UNSAFE_className="scroll-container"
              UNSAFE_style={{
                width: 'calc(6 * 32px + 5 * 4px)',
                overflow: 'hidden',
                position: 'relative'
              }}
            >
              <div
                className="quarters-scroll"
                style={{
                  display: 'flex',
                  width: `${
                    quartersArray.length * (QUARTER_WIDTH + QUARTER_GAP)
                  }`,
                  position: 'relative',
                  whiteSpace: 'nowrap',
                  zIndex: 1,
                  opacity: disableYearsAndQuarters ? '0.5' : '1',
                  pointerEvents: disableYearsAndQuarters ? 'none' : 'auto'
                }}
              >
                {quarterRange && (
                  <div
                    className="range-bg"
                    style={{
                      position: 'absolute',
                      left: quarterRange.left,
                      width: quarterRange.width,
                      height: 32,
                      background: 'rgba(2, 101, 220, 0.1)',
                      borderRadius: 16,
                      zIndex: 0,
                      pointerEvents: 'none'
                    }}
                  />
                )}
                {quartersArray
                  .slice(displayedQuarterIndex, displayedQuarterIndex + 6)
                  .map((quarterData, i) => {
                    const actualIndex = displayedQuarterIndex + i;
                    return (
                      <Quarter
                        key={`${quarterData.quarter}-${quarterData.year}`}
                        quarter={quarterData.quarter}
                        startQuarter={startQuarter}
                        endQuarter={endQuarter}
                        hoveringQuarter={hoveringQuarter}
                        activeQuarter={activeQuarter}
                        onQuarterClick={handleQuarterClick}
                        onQuarterHover={handleQuarterHover}
                        year={quarterData.year}
                        mode={mode}
                      />
                    );
                  })}
              </div>
            </View>
            <ActionButton
              aria-label="Next year quarters"
              UNSAFE_className="quarter-scroller"
              data-testid="calendar-quarters-next"
              isQuiet
              isDisabled={
                disableYearsAndQuarters ||
                displayedQuarterIndex >= quartersArray.length - 6
              }
              onPress={() =>
                !disableYearsAndQuarters && handleScrollQuarters('right')
              }
            >
              <RightArrowIcon size="L" />
            </ActionButton>
          </Flex>
        </View>
      )}
      {!hideMonthList && (
        <View UNSAFE_className="months">
          <Flex alignItems="center" gap="size-10">
            <ActionButton
              aria-label="Previous month"
              UNSAFE_className="month-scroller"
              data-testid="calendar-months-previous"
              isQuiet
              onPress={() => handleScrollMonths('left')}
              isDisabled={displayedMonthIndex <= 0}
            >
              <LeftArrowIcon size="L" />
            </ActionButton>
            <View
              UNSAFE_className="scroll-container"
              UNSAFE_style={{
                width: 'calc(6 * 32px + 5 * 4px + 4px)',
                overflow: 'hidden',
                overflowY: 'hidden',
                position: 'relative',
                height: '32px'
              }}
            >
              <View
                UNSAFE_className="months-scroll"
                UNSAFE_style={{
                  display: 'flex',
                  width: 'calc(6 * 32px + 5 * 4px)',
                  position: 'relative',
                  whiteSpace: 'nowrap',
                  zIndex: 1,
                  overflowY: 'hidden',
                  height: '32px',
                  gap: '4px'
                }}
              >
                {mode !== 'single' && monthRange && (
                  <div
                    className="range-bg"
                    style={{
                      position: 'absolute',
                      left: monthRange.left,
                      width: monthRange.width,
                      height: 32,
                      background: 'rgba(2, 101, 220, 0.1)',
                      borderRadius: 16,
                      zIndex: 0,
                      pointerEvents: 'none'
                    }}
                  />
                )}
                {monthsArray
                  .slice(displayedMonthIndex, displayedMonthIndex + 6)
                  .map((monthData, i) => {
                    const actualIndex = displayedMonthIndex + i;
                    return (
                      <Month
                        key={actualIndex}
                        month={monthData.month}
                        monthIndex={monthData.monthIndex}
                        year={monthData.year}
                        startMonth={startMonth}
                        endMonth={endMonth}
                        startYear={startMonthYear}
                        endYear={endMonthYear}
                        onMonthClick={handleMonthClick}
                        onMonthHover={handleMonthHover}
                        hoveringMonth={hoveringMonth}
                        hoveringYear={hoveringMonthYear}
                        mode={mode}
                      />
                    );
                  })}
              </View>
            </View>
            <ActionButton
              aria-label="Next month"
              UNSAFE_className="month-scroller"
              data-testid="calendar-months-next"
              isQuiet
              onPress={() => handleScrollMonths('right')}
              isDisabled={displayedMonthIndex >= monthsArray.length - 6}
            >
              <RightArrowIcon size="L" />
            </ActionButton>
          </Flex>
        </View>
      )}
      {mode === 'range' ? (
        <RangeCalendar
          value={range}
          onChange={handleRangeChange}
          visibleMonths={1}
          data-testid="calendar-range-picker"
          UNSAFE_className="calendar-range-calendar"
          minValue={
            disablePastDate
              ? new CalendarDate(
                  new Date().getFullYear(),
                  new Date().getMonth() + 1,
                  new Date().getDate()
                )
              : undefined
          }
        />
      ) : (
        <div style={{ minHeight: '290px' }}>
          <Calendar
            value={selectedDate}
            onChange={handleDateChange}
            visibleMonths={1}
            data-testid="calendar-single-picker"
            UNSAFE_className="calendar-single-calendar"
            focusedValue={focusedValue}
            onFocusChange={onFocusChange}
            data-quarter-selected={startQuarter !== null}
            data-month-selected={startMonth !== null}
            minValue={
              disablePastDate
                ? new CalendarDate(
                    new Date().getFullYear(),
                    new Date().getMonth() + 1,
                    new Date().getDate()
                  )
                : undefined
            }
          />
        </div>
      )}
    </View>
  );
};

export function CalendarRange({
  defaultRange,
  defaultDate,
  defaultQuarter,
  defaultMonth,
  onCalendarRangeChange,
  onCalendarDateChange,
  skipTriggerButton = false,
  disableYearsAndQuarters = false,
  hideYearList = false,
  hideQuarterList = false,
  hideMonthList = false,
  showTBDNA = false, // Default to false - hidden by default
  mode = 'range',
  defaultSpecialValue = null,
  disablePastDate = false,
  enforceEndDateAfterStart = false,
  disableNA = false,
  disableTBD = false
}: CalendarRangeProps) {
  const [range, setRange] = useState<DateRange | undefined>(defaultRange);
  const [activeYear, setActiveYear] = useState<number | null>(null);
  const [displayedYear, setDisplayedYear] = useState(new Date().getFullYear());
  const [scrollPosition, setScrollPosition] = useState(0);

  const [activeMonth, setActiveMonth] = useState<number | null>(null);
  const [activeQuarter, setActiveQuarter] = useState<number | null>(
    getQuarter(new Date())
  );
  const [startQuarter, setStartQuarter] = useState<{
    quarter: number;
    year: number;
  } | null>(defaultQuarter || null);
  const [endQuarter, setEndQuarter] = useState<{
    quarter: number;
    year: number;
  } | null>(null);
  const [hoveringQuarter, setHoveringQuarter] = useState<{
    quarter: number;
    year: number;
  } | null>(null);

  // Month selection state
  const [startMonth, setStartMonth] = useState<number | null>(
    defaultMonth
      ? new Date(`${defaultMonth.month} 1, ${defaultMonth.year}`).getMonth()
      : null
  );
  const [endMonth, setEndMonth] = useState<number | null>(null);
  const [hoveringMonth, setHoveringMonth] = useState<number | null>(null);
  const [startMonthYear, setStartMonthYear] = useState<number | null>(
    defaultMonth ? defaultMonth.year : null
  );

  const [endMonthYear, setEndMonthYear] = useState<number | null>(null);
  const [hoveringMonthYear, setHoveringMonthYear] = useState<number | null>(
    null
  );

  const quartersContainerRef = useRef<HTMLDivElement | null>(null);

  const currentYear = new Date().getFullYear();
  const [displayedQuarterYear, setDisplayedQuarterYear] = useState(currentYear);

  const [displayedMonthIndex, setDisplayedMonthIndex] = useState(
    getCurrentMonthPosition()
  );

  const [displayedQuarterIndex, setDisplayedQuarterIndex] = useState(
    getCurrentQuarterPosition()
  );

  const [selectedDate, setSelectedDate] = useState<CalendarDate | undefined>(
    defaultDate
  );

  const [specialValue, setSpecialValue] = useState<'TBD' | 'N/A' | null>(
    defaultSpecialValue
  );

  // Ref to track the last applied defaultRange values to avoid infinite loops when parent passes a new object reference every render
  const lastAppliedRangeRef = useRef<string | null>(null);

  useEffect(() => {
    if (defaultRange) {
      const rangeKey = `${defaultRange.start?.toString() ?? ''}-${
        defaultRange.end?.toString() ?? ''
      }`;
      if (lastAppliedRangeRef.current !== rangeKey) {
        lastAppliedRangeRef.current = rangeKey;
        setRange(defaultRange);
      }
    }
  }, [defaultRange]);

  // Ref to track the last applied defaultQuarter values to avoid unnecessary updates
  const lastAppliedQuarterRef = useRef<{
    quarter: number;
    year: number;
  } | null>(defaultQuarter || null);

  useEffect(() => {
    if (defaultQuarter) {
      // Only update if the quarter VALUES actually changed (not just reference)
      const isSameQuarter =
        lastAppliedQuarterRef.current &&
        lastAppliedQuarterRef.current.quarter === defaultQuarter.quarter &&
        lastAppliedQuarterRef.current.year === defaultQuarter.year;

      if (!isSameQuarter) {
        lastAppliedQuarterRef.current = defaultQuarter;
        setStartQuarter(defaultQuarter);
        setEndQuarter(null);
        // Clear other selections
        setStartMonth(null);
        setEndMonth(null);
        setStartMonthYear(null);
        setEndMonthYear(null);
        setSelectedDate(undefined);
      }
    }
  }, [defaultQuarter]);

  // Ref to track the last applied defaultMonth values to avoid unnecessary updates
  const lastAppliedMonthRef = useRef<{ month: string; year: number } | null>(
    defaultMonth || null
  );

  useEffect(() => {
    if (defaultMonth) {
      // Only update if the month VALUES actually changed (not just reference)
      const isSameMonth =
        lastAppliedMonthRef.current &&
        lastAppliedMonthRef.current.month === defaultMonth.month &&
        lastAppliedMonthRef.current.year === defaultMonth.year;

      if (!isSameMonth) {
        lastAppliedMonthRef.current = defaultMonth;
        const monthIndex = new Date(
          `${defaultMonth.month} 1, ${defaultMonth.year}`
        ).getMonth();
        setStartMonth(monthIndex);
        setStartMonthYear(defaultMonth.year);
        setEndMonth(null);
        setEndMonthYear(null);
        setHoveringMonth(null);
        setHoveringMonthYear(null);
        // Clear other selections
        setStartQuarter(null);
        setEndQuarter(null);
        setSelectedDate(undefined);
      }
    }
  }, [defaultMonth]);

  useEffect(() => {
    if (defaultSpecialValue) {
      setSpecialValue(defaultSpecialValue);
      // Clear other selections
      setSelectedDate(undefined);
      setStartQuarter(null);
      setEndQuarter(null);
      setStartMonth(null);
      setEndMonth(null);
      setStartMonthYear(null);
      setEndMonthYear(null);
      setRange(undefined);
    }
  }, [defaultSpecialValue]);

  const handleRangeChange = (newRange: DateRange) => {
    // Clear other selection states
    setStartQuarter(null);
    setEndQuarter(null);
    setHoveringQuarter(null);
    setStartMonth(null);
    setEndMonth(null);
    setHoveringMonth(null);
    setStartMonthYear(null);
    setEndMonthYear(null);
    setHoveringMonthYear(null);
    setActiveYear(null);

    // Ensure end date is at least one day after start date if enforceEndDateAfterStart is true
    // BUT: If user selects the same date twice, keep it as the same date (don't auto-increment)
    if (enforceEndDateAfterStart && newRange.start && newRange.end) {
      const startDate = new Date(
        newRange.start.year,
        newRange.start.month - 1,
        newRange.start.day
      );
      const endDate = new Date(
        newRange.end.year,
        newRange.end.month - 1,
        newRange.end.day
      );

      // Check if dates are exactly the same (user double-clicked same date)
      const isSameDate =
        newRange.start.year === newRange.end.year &&
        newRange.start.month === newRange.end.month &&
        newRange.start.day === newRange.end.day;

      if (startDate > endDate && !isSameDate) {
        // Only auto-increment if start is after end AND they're not the same date
        const nextDay = new Date(startDate);
        nextDay.setDate(nextDay.getDate() + 1);
        newRange = {
          start: newRange.start,
          end: new CalendarDate(
            nextDay.getFullYear(),
            nextDay.getMonth() + 1,
            nextDay.getDate()
          )
        };
      }
      // If same date, keep it as is (both start and end will be the same date)
    }

    setRange(newRange);
    if (onCalendarRangeChange) {
      onCalendarRangeChange(newRange);
    }
  };

  const handleYearClick = (year: number) => {
    setStartQuarter(null);
    setEndQuarter(null);
    setHoveringQuarter(null);
    setStartMonth(null);
    setEndMonth(null);
    setHoveringMonth(null);
    setStartMonthYear(null);
    setEndMonthYear(null);
    setHoveringMonthYear(null);
    setActiveMonth(-1);
    setActiveYear(year);
    setDisplayedYear(year);
    const newRange = getDynamicDates(year);
    if (onCalendarRangeChange) {
      onCalendarRangeChange(newRange);
    }
    setRange(newRange);
  };

  const handleQuarterClick = (quarter: number, year: number) => {
    if (mode === 'single') {
      // For single mode, set startQuarter to show highlighting
      setStartQuarter({ quarter, year });
      setEndQuarter(null);
      setStartMonth(null);
      setEndMonth(null);
      setStartMonthYear(null);
      setEndMonthYear(null);
      setSelectedDate(undefined); // Clear the selected date when selecting a quarter

      const quarterDates = getQuarterDates(year, quarter);
      if (onCalendarDateChange) {
        onCalendarDateChange(quarterDates.start, true, { quarter, year });
      }
      return;
    }

    // Original range selection logic
    if (startQuarter === null) {
      setStartQuarter({ quarter, year });
      setActiveQuarter(quarter);
      setStartMonth(null);
      setEndMonth(null);
      setHoveringMonth(null);
      setStartMonthYear(null);
      setEndMonthYear(null);
      setHoveringMonthYear(null);
      setActiveYear(null);
    } else if (startQuarter !== null && endQuarter === null) {
      setEndQuarter({ quarter, year });
      setActiveQuarter(quarter);
      setStartMonth(null);
      setEndMonth(null);
      setHoveringMonth(null);
      setStartMonthYear(null);
      setEndMonthYear(null);
      setHoveringMonthYear(null);
      setActiveYear(null);
    } else {
      setStartQuarter({ quarter, year });
      setEndQuarter(null);
      setHoveringQuarter(null);
      setActiveQuarter(quarter);
      setStartMonth(null);
      setEndMonth(null);
      setHoveringMonth(null);
      setStartMonthYear(null);
      setEndMonthYear(null);
      setHoveringMonthYear(null);
      setActiveYear(null);
    }
  };

  const handleQuarterHover = (quarter: number | null, year: number | null) => {
    if (startQuarter === null) {
      return;
    }

    if (endQuarter === null && quarter !== null && year !== null) {
      setHoveringQuarter({ quarter, year });
    } else if (quarter === null) {
      setHoveringQuarter(null);
    }
  };

  useEffect(() => {
    if (startQuarter != null && endQuarter != null) {
      // Sort quarters by year and quarter number
      const sortedQuarters = [startQuarter, endQuarter].sort((a, b) => {
        if (a.year !== b.year) {
          return a.year - b.year;
        }
        return a.quarter - b.quarter;
      });

      const newRange = {
        start: getQuarterDates(
          sortedQuarters[0].year,
          sortedQuarters[0].quarter
        ).start,
        end: getQuarterDates(sortedQuarters[1].year, sortedQuarters[1].quarter)
          .end
      };

      if (onCalendarRangeChange) {
        onCalendarRangeChange(newRange);
      }
      setRange(newRange);
    }
  }, [startQuarter, endQuarter]);

  const handleMonthClick = (month: number, year: number) => {
    if (mode === 'single') {
      setStartQuarter(null);
      setEndQuarter(null);
      setHoveringQuarter(null);
      setStartMonth(month);
      setStartMonthYear(year);
      setEndMonth(null);
      setEndMonthYear(null);
      setHoveringMonth(null);
      setHoveringMonthYear(null);
      setActiveMonth(month);
      setActiveYear(null);

      // Get the first day of the month
      const monthDate = parseDate(
        `${year}-${String(month + 1).padStart(2, '0')}-01`
      );
      const monthName =
        monthsArray.find(m => m.monthIndex === month)?.month || '';

      if (onCalendarDateChange) {
        // Pass both isMonthSelection and selectedMonth
        onCalendarDateChange(monthDate, false, undefined, true, {
          month: monthName,
          year
        });
      }
      setSelectedDate(undefined); // Clear the selected date when selecting a month
      return;
    }

    // Original range selection logic remains the same
    if (startMonth == null) {
      setStartMonth(month);
      setStartMonthYear(year);
      setActiveMonth(month);
      setActiveYear(null);
    } else if (startMonth != null && endMonth == null) {
      setEndMonth(month);
      setEndMonthYear(year);
      setStartQuarter(null);
      setEndQuarter(null);
      setHoveringQuarter(null);
      setActiveYear(null);
    } else {
      setStartMonth(month);
      setStartMonthYear(year);
      setEndMonth(null);
      setEndMonthYear(null);
      setHoveringMonth(null);
      setHoveringMonthYear(null);
      setActiveMonth(month);
      setActiveYear(null);
    }
  };

  useEffect(() => {
    if (
      startMonth != null &&
      endMonth != null &&
      startMonthYear != null &&
      endMonthYear != null
    ) {
      let newStartDate, newEndDate;

      // Sort months chronologically
      const sortedMonths = [
        { month: startMonth, year: startMonthYear },
        { month: endMonth, year: endMonthYear }
      ].sort((a, b) => {
        if (a.year !== b.year) return a.year - b.year;
        return a.month - b.month;
      });

      newStartDate = getMonthDates(sortedMonths[0].year, sortedMonths[0].month);
      newEndDate = getMonthDates(sortedMonths[1].year, sortedMonths[1].month);

      const newRange = {
        start: newStartDate.start,
        end: newEndDate.end
      };
      if (onCalendarRangeChange) {
        onCalendarRangeChange(newRange);
      }
      setRange(newRange);
    }
  }, [startMonth, endMonth, startMonthYear, endMonthYear]);

  const handleMonthHover = (month: number, year: number) => {
    if (startMonth == null) {
      return;
    }

    if (endMonth == null) {
      setHoveringMonth(month);
      setHoveringMonthYear(year);
    }
  };

  const handleSpecialValueChange = (value: 'TBD' | 'N/A' | null) => {
    setSpecialValue(value);
    if (value) {
      // Clear other selections when TBD/N/A is selected
      setSelectedDate(undefined);
      setStartQuarter(null);
      setEndQuarter(null);
      setStartMonth(null);
      setEndMonth(null);
      setStartMonthYear(null);
      setEndMonthYear(null);
      setRange(undefined);
    }

    if (onCalendarDateChange) {
      // Pass the special value to the parent
      onCalendarDateChange(
        parseDate('2000-01-01'), // Dummy date, will be ignored
        false,
        undefined,
        false,
        undefined,
        value
      );
    }
  };

  const handleScrollQuarters = (direction: 'left' | 'right') => {
    // Scroll through all quarters one by one for precise navigation
    // Total quarters: (YEARS_BACK + 1 + YEARS_FORWARD) * 4, one fiscal year's worth of quarters
    const change = direction === 'left' ? -1 : 1;
    const maxStartIndex = Math.max(0, quartersArray.length - 6);
    const newIndex = Math.max(
      0,
      Math.min(displayedQuarterIndex + change, maxStartIndex)
    );

    // Only update if the new index is different
    if (newIndex !== displayedQuarterIndex) {
      setDisplayedQuarterIndex(newIndex);
    }
  };

  const handleScrollMonths = (direction: 'left' | 'right') => {
    const change = direction === 'left' ? -6 : 6; // Scroll by 6 months
    const maxStartIndex = Math.max(0, monthsArray.length - 6);
    const newIndex = Math.max(
      0,
      Math.min(displayedMonthIndex + change, maxStartIndex)
    );

    if (newIndex !== displayedMonthIndex) {
      setDisplayedMonthIndex(newIndex);
    }
  };
  // Reset scroll position when component mounts or year changes
  useEffect(() => {
    setScrollPosition(0);
  }, []);

  const chevronLeft = <ChevronLeft size="L" />;
  const chevronRight = <ChevronRight size="L" />;

  // For months
  const MONTH_WIDTH = 32;
  const MONTH_GAP = 4;

  const monthsArray = getCurrentMonthsArray();

  const getMonthRangeIndices = () => {
    let rangeStart: { month: number; year: number } | null = null,
      rangeEnd: { month: number; year: number } | null = null;
    if (
      startMonth !== null &&
      endMonth !== null &&
      startMonthYear !== null &&
      endMonthYear !== null
    ) {
      rangeStart = { month: startMonth, year: startMonthYear };
      rangeEnd = { month: endMonth, year: endMonthYear };
    } else if (
      startMonth !== null &&
      endMonth === null &&
      hoveringMonth !== null &&
      startMonthYear !== null &&
      hoveringMonthYear !== null
    ) {
      rangeStart = { month: startMonth, year: startMonthYear };
      rangeEnd = { month: hoveringMonth, year: hoveringMonthYear };
    }
    if (
      rangeStart !== null &&
      rangeEnd !== null &&
      (rangeStart.month !== rangeEnd.month || rangeStart.year !== rangeEnd.year)
    ) {
      const visibleMonths = monthsArray.slice(
        displayedMonthIndex,
        displayedMonthIndex + 6
      );

      // Find indices within the visible range
      const startIdx = visibleMonths.findIndex(
        m => m.monthIndex === rangeStart!.month && m.year === rangeStart!.year
      );
      const endIdx = visibleMonths.findIndex(
        m => m.monthIndex === rangeEnd!.month && m.year === rangeEnd!.year
      );

      // Calculate background range for visible portion only
      let left = 0,
        width = 0;

      if (startIdx !== -1 && endIdx !== -1) {
        // Both start and end are visible
        left =
          Math.min(startIdx, endIdx) * (MONTH_WIDTH + MONTH_GAP) +
          MONTH_WIDTH / 2;
        width = Math.abs(endIdx - startIdx) * (MONTH_WIDTH + MONTH_GAP);
      } else if (startIdx !== -1 && endIdx === -1) {
        // Only start is visible, range extends beyond visible area
        // Determine if range extends to the right or left
        const rangeStartComparable = rangeStart.year * 12 + rangeStart.month;
        const rangeEndComparable = rangeEnd.year * 12 + rangeEnd.month;

        if (rangeEndComparable > rangeStartComparable) {
          // Range extends to the right
          left = startIdx * (MONTH_WIDTH + MONTH_GAP) + MONTH_WIDTH / 2;
          width = (5 - startIdx + 1) * (MONTH_WIDTH + MONTH_GAP);
        } else {
          // Range extends to the left
          left = 0;
          width = (startIdx + 1) * (MONTH_WIDTH + MONTH_GAP) - MONTH_WIDTH / 2;
        }
      } else if (startIdx === -1 && endIdx !== -1) {
        // Only end is visible, range starts before visible area
        // Determine if range extends to the right or left
        const rangeStartComparable = rangeStart.year * 12 + rangeStart.month;
        const rangeEndComparable = rangeEnd.year * 12 + rangeEnd.month;

        if (rangeEndComparable > rangeStartComparable) {
          // Range starts before visible area
          left = 0;
          width = (endIdx + 1) * (MONTH_WIDTH + MONTH_GAP) - MONTH_WIDTH / 2;
        } else {
          // Range starts after visible area
          left = endIdx * (MONTH_WIDTH + MONTH_GAP) + MONTH_WIDTH / 2;
          width = (5 - endIdx + 1) * (MONTH_WIDTH + MONTH_GAP);
        }
      } else {
        // Neither start nor end visible, but range spans across entire visible area
        // Check if the visible range is within the selected range
        const visibleStartMonth = visibleMonths[0];
        const visibleEndMonth = visibleMonths[5];

        // Convert to comparable format for range checking
        const rangeStartComparable = rangeStart.year * 12 + rangeStart.month;
        const rangeEndComparable = rangeEnd.year * 12 + rangeEnd.month;
        const visibleStartComparable =
          visibleStartMonth.year * 12 + visibleStartMonth.monthIndex;
        const visibleEndComparable =
          visibleEndMonth.year * 12 + visibleEndMonth.monthIndex;

        const sortedRange =
          rangeStartComparable <= rangeEndComparable
            ? [rangeStartComparable, rangeEndComparable]
            : [rangeEndComparable, rangeStartComparable];

        if (
          visibleStartComparable >= sortedRange[0] &&
          visibleEndComparable <= sortedRange[1]
        ) {
          // Entire visible range is within selection
          left = 0;
          width = 6 * (MONTH_WIDTH + MONTH_GAP) - MONTH_GAP;
        }
      }

      if (width > 0) {
        return { left, width };
      }
    }
    return null;
  };
  const monthRange = getMonthRangeIndices();

  // For quarters
  const QUARTER_WIDTH = 32;
  const QUARTER_GAP = 4;

  const quartersArray = getCurrentQuartersArray();

  // Ensure displayedQuarterIndex is correct based on the actual quarters array
  // This is a safety net to ensure the current quarter is always visible
  // The initial calculation should be correct, but this handles edge cases
  useEffect(() => {
    const { fiscalQuarter, fiscalYear } = getCurrentFiscalQuarterAndYear();
    const currentQuarterIndex = quartersArray.findIndex(
      q => q.quarter === fiscalQuarter && q.year === fiscalYear
    );

    // If current quarter is found in array and not visible, update to show it first
    if (currentQuarterIndex !== -1) {
      const isCurrentQuarterVisible =
        displayedQuarterIndex <= currentQuarterIndex &&
        currentQuarterIndex < displayedQuarterIndex + 6;

      if (!isCurrentQuarterVisible) {
        setDisplayedQuarterIndex(currentQuarterIndex);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount - initial calculation handles the rest

  const getQuarterRangeIndices = () => {
    let rangeStart: { quarter: number; year: number } | null = null,
      rangeEnd: { quarter: number; year: number } | null = null;
    if (startQuarter && endQuarter) {
      rangeStart = startQuarter;
      rangeEnd = endQuarter;
    } else if (startQuarter && !endQuarter && hoveringQuarter) {
      rangeStart = startQuarter;
      rangeEnd = hoveringQuarter;
    }
    if (
      rangeStart &&
      rangeEnd &&
      (rangeStart.quarter !== rangeEnd.quarter ||
        rangeStart.year !== rangeEnd.year)
    ) {
      const visibleQuarters = quartersArray.slice(
        displayedQuarterIndex,
        displayedQuarterIndex + 6
      );

      // Find indices within the visible range
      const startIdx = visibleQuarters.findIndex(
        q => q.quarter === rangeStart!.quarter && q.year === rangeStart!.year
      );
      const endIdx = visibleQuarters.findIndex(
        q => q.quarter === rangeEnd!.quarter && q.year === rangeEnd!.year
      );

      // Calculate background range for visible portion only
      let left = 0,
        width = 0;

      if (startIdx !== -1 && endIdx !== -1) {
        // Both start and end are visible
        left =
          Math.min(startIdx, endIdx) * (QUARTER_WIDTH + QUARTER_GAP) +
          QUARTER_WIDTH / 2;
        width = Math.abs(endIdx - startIdx) * (QUARTER_WIDTH + QUARTER_GAP);
      } else if (startIdx !== -1 && endIdx === -1) {
        // Only start is visible, range extends beyond visible area
        // Determine if range extends to the right or left
        const rangeStartComparable = rangeStart.year * 4 + rangeStart.quarter;
        const rangeEndComparable = rangeEnd.year * 4 + rangeEnd.quarter;

        if (rangeEndComparable > rangeStartComparable) {
          // Range extends to the right
          left = startIdx * (QUARTER_WIDTH + QUARTER_GAP) + QUARTER_WIDTH / 2;
          width = (5 - startIdx + 1) * (QUARTER_WIDTH + QUARTER_GAP);
        } else {
          // Range extends to the left
          left = 0;
          width =
            (startIdx + 1) * (QUARTER_WIDTH + QUARTER_GAP) - QUARTER_WIDTH / 2;
        }
      } else if (startIdx === -1 && endIdx !== -1) {
        // Only end is visible, range starts before visible area
        // Determine if range extends to the right or left
        const rangeStartComparable = rangeStart.year * 4 + rangeStart.quarter;
        const rangeEndComparable = rangeEnd.year * 4 + rangeEnd.quarter;

        if (rangeEndComparable > rangeStartComparable) {
          // Range starts before visible area
          left = 0;
          width =
            (endIdx + 1) * (QUARTER_WIDTH + QUARTER_GAP) - QUARTER_WIDTH / 2;
        } else {
          // Range starts after visible area
          left = endIdx * (QUARTER_WIDTH + QUARTER_GAP) + QUARTER_WIDTH / 2;
          width = (5 - endIdx + 1) * (QUARTER_WIDTH + QUARTER_GAP);
        }
      } else {
        // Neither start nor end visible, but range spans across entire visible area
        // Check if the visible range is within the selected range
        const visibleStartQuarter = visibleQuarters[0];
        const visibleEndQuarter = visibleQuarters[5];

        // Convert to comparable format for range checking (year * 4 + quarter for fiscal quarters)
        const rangeStartComparable = rangeStart.year * 4 + rangeStart.quarter;
        const rangeEndComparable = rangeEnd.year * 4 + rangeEnd.quarter;
        const visibleStartComparable =
          visibleStartQuarter.year * 4 + visibleStartQuarter.quarter;
        const visibleEndComparable =
          visibleEndQuarter.year * 4 + visibleEndQuarter.quarter;

        const sortedRange =
          rangeStartComparable <= rangeEndComparable
            ? [rangeStartComparable, rangeEndComparable]
            : [rangeEndComparable, rangeStartComparable];

        if (
          visibleStartComparable >= sortedRange[0] &&
          visibleEndComparable <= sortedRange[1]
        ) {
          // Entire visible range is within selection
          left = 0;
          width = 6 * (QUARTER_WIDTH + QUARTER_GAP) - QUARTER_GAP;
        }
      }

      if (width > 0) {
        return { left, width };
      }
    }
    return null;
  };
  const quarterRange = getQuarterRangeIndices();

  const handleDateChange = (date: CalendarDate) => {
    setSelectedDate(date);
    // Clear quarter and month selections when a date is selected
    setStartQuarter(null);
    setEndQuarter(null);
    setStartMonth(null);
    setEndMonth(null);
    setStartMonthYear(null);
    setEndMonthYear(null);
    if (onCalendarDateChange) {
      onCalendarDateChange(date);
    }
  };

  const isInRange = (quarter: number, year: number) =>
    startQuarter &&
    (year > startQuarter.year ||
      (year === startQuarter.year && quarter >= startQuarter.quarter)) &&
    (endQuarter
      ? year < endQuarter.year ||
        (year === endQuarter.year && quarter <= endQuarter.quarter)
      : hoveringQuarter
      ? year < hoveringQuarter.year ||
        (year === hoveringQuarter.year && quarter <= hoveringQuarter.quarter)
      : false);

  return (
    <View>
      {!skipTriggerButton && (
        <MenuTrigger>
          <ActionButton
            UNSAFE_className="calendar-button"
            data-testid="calendar-trigger-button"
          >
            <CalendarIcon />
            <Text UNSAFE_className="calendar-text">
              {specialValue ||
                (mode === 'range' ? (
                  <>
                    {range
                      ? `${formatCalendarDate(
                          range.start
                        )} - ${formatCalendarDate(range.end)}`
                      : 'Select date range'}
                  </>
                ) : selectedDate ? (
                  formatCalendarDate(selectedDate)
                ) : (
                  'Select date'
                ))}
            </Text>
          </ActionButton>
          <CalendarContent
            displayedQuarterIndex={displayedQuarterIndex}
            displayedMonthIndex={displayedMonthIndex}
            disableYearsAndQuarters={disableYearsAndQuarters}
            hideYearList={hideYearList}
            hideQuarterList={hideQuarterList}
            hideMonthList={hideMonthList}
            handleScrollQuarters={handleScrollQuarters}
            handleScrollMonths={handleScrollMonths}
            quartersArray={quartersArray}
            monthsArray={monthsArray}
            quarterRange={quarterRange}
            monthRange={monthRange}
            startQuarter={startQuarter}
            endQuarter={endQuarter}
            hoveringQuarter={hoveringQuarter}
            activeQuarter={activeQuarter}
            handleQuarterClick={handleQuarterClick}
            handleQuarterHover={handleQuarterHover}
            startMonth={startMonth}
            endMonth={endMonth}
            startMonthYear={startMonthYear}
            endMonthYear={endMonthYear}
            hoveringMonth={hoveringMonth}
            hoveringMonthYear={hoveringMonthYear}
            handleMonthClick={handleMonthClick}
            handleMonthHover={handleMonthHover}
            mode={mode}
            range={range}
            handleRangeChange={handleRangeChange}
            selectedDate={selectedDate}
            handleDateChange={handleDateChange}
            handleYearClick={handleYearClick}
            activeYear={activeYear}
            showTBDNA={showTBDNA}
            specialValue={specialValue}
            onSpecialValueChange={handleSpecialValueChange}
            isDisabled={disableYearsAndQuarters}
            disablePastDate={disablePastDate}
            disableNA={disableNA}
            disableTBD={disableTBD}
          />
        </MenuTrigger>
      )}
      {skipTriggerButton && (
        <CalendarContent
          displayedQuarterIndex={displayedQuarterIndex}
          displayedMonthIndex={displayedMonthIndex}
          disableYearsAndQuarters={disableYearsAndQuarters}
          hideYearList={hideYearList}
          hideQuarterList={hideQuarterList}
          hideMonthList={hideMonthList}
          handleScrollQuarters={handleScrollQuarters}
          handleScrollMonths={handleScrollMonths}
          quartersArray={quartersArray}
          monthsArray={monthsArray}
          quarterRange={quarterRange}
          monthRange={monthRange}
          startQuarter={startQuarter}
          endQuarter={endQuarter}
          hoveringQuarter={hoveringQuarter}
          activeQuarter={activeQuarter}
          handleQuarterClick={handleQuarterClick}
          handleQuarterHover={handleQuarterHover}
          startMonth={startMonth}
          endMonth={endMonth}
          startMonthYear={startMonthYear}
          endMonthYear={endMonthYear}
          hoveringMonth={hoveringMonth}
          hoveringMonthYear={hoveringMonthYear}
          handleMonthClick={handleMonthClick}
          handleMonthHover={handleMonthHover}
          mode={mode}
          range={range}
          handleRangeChange={handleRangeChange}
          selectedDate={selectedDate}
          handleDateChange={handleDateChange}
          handleYearClick={handleYearClick}
          activeYear={activeYear}
          showTBDNA={showTBDNA}
          specialValue={specialValue}
          onSpecialValueChange={handleSpecialValueChange}
          isDisabled={disableYearsAndQuarters}
          disablePastDate={disablePastDate}
          disableNA={disableNA}
          disableTBD={disableTBD}
        />
      )}
    </View>
  );
}
